import { useMemo, useState } from "react";
import { Pencil, Plus, Search, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ManualTransaction,
  SCHEDULE_E_CATEGORIES,
  useAccounting,
} from "@/hooks/use-accounting";
import { fmtMoney } from "@/components/accounting/accounting-format";
import { TransactionFormDialog } from "@/components/accounting/TransactionFormDialog";
import { cn } from "@/lib/utils";

interface Props {
  year: number;
}

const catLabel = (key: string) => SCHEDULE_E_CATEGORIES.find((c) => c.key === key)?.label ?? key;
const catLine = (key: string) => SCHEDULE_E_CATEGORIES.find((c) => c.key === key)?.line ?? "—";

export function TransactionsTab({ year }: Props) {
  const { allTransactions, manualTransactions, properties, deleteManualTransaction } = useAccounting(year);

  const [search, setSearch] = useState("");
  const [property, setProperty] = useState("all");
  const [category, setCategory] = useState("all");
  const [type, setType] = useState("all");
  const [source, setSource] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ManualTransaction | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allTransactions.filter((t) => {
      if (property !== "all" && t.pipeline_property_id !== property) return false;
      if (category !== "all" && t.category !== category) return false;
      if (type !== "all" && t.type !== type) return false;
      if (source === "manual" && t.source !== "Manual") return false;
      if (source === "auto" && t.source === "Manual") return false;
      if (q) {
        const haystack = `${t.description} ${t.property_label ?? ""} ${catLabel(t.category)}`.toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      return true;
    });
  }, [allTransactions, search, property, category, type, source]);

  const totals = useMemo(() => {
    const income = filtered.filter((t) => t.type === "income").reduce((s, t) => s + t.amount, 0);
    const expense = filtered.filter((t) => t.type === "expense").reduce((s, t) => s + t.amount, 0);
    return { income, expense, net: income - expense };
  }, [filtered]);

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (manualId: string) => {
    const mt = manualTransactions.find((m) => m.id === manualId);
    if (mt) {
      setEditing(mt);
      setDialogOpen(true);
    }
  };

  return (
    <div className="space-y-3.5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="relative min-w-[220px] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search description, property, category..."
            className="pl-9"
          />
        </div>
        <Select value={property} onValueChange={setProperty}>
          <SelectTrigger className="w-auto min-w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All properties</SelectItem>
            {properties.map((p) => (
              <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-auto min-w-[150px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {SCHEDULE_E_CATEGORIES.map((c) => (
              <SelectItem key={c.key} value={c.key}>Line {c.line} · {c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-auto min-w-[120px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="income">Income only</SelectItem>
            <SelectItem value="expense">Expenses only</SelectItem>
          </SelectContent>
        </Select>
        <Select value={source} onValueChange={setSource}>
          <SelectTrigger className="w-auto min-w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="auto">Auto-aggregated</SelectItem>
            <SelectItem value="manual">Manual entries</SelectItem>
          </SelectContent>
        </Select>
        <span className="flex-1" />
        <Button onClick={openAdd}>
          <Plus className="mr-1.5 h-4 w-4" /> Add transaction
        </Button>
      </div>

      <Card className="overflow-hidden p-0">
        {filtered.length === 0 ? (
          <p className="m-4 rounded-lg border border-dashed border-border px-5 py-12 text-center text-sm text-muted-foreground">
            No transactions match the current filters.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Property</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((t, i) => {
                  const isManual = t.source === "Manual";
                  return (
                    <TableRow key={t.manual_id ?? `${t.source}-${t.category}-${t.date}-${i}`}>
                      <TableCell className="whitespace-nowrap text-sm">{t.date}</TableCell>
                      <TableCell>
                        <p className="text-sm font-semibold text-foreground">{t.property_label ?? "Portfolio-wide"}</p>
                        {t.description && <p className="mt-0.5 text-[11px] text-muted-foreground">{t.description}</p>}
                      </TableCell>
                      <TableCell>
                        <p className="text-sm">{catLabel(t.category)}</p>
                        <p className="text-[11px] text-muted-foreground">Schedule E line {catLine(t.category)}</p>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] uppercase",
                            isManual
                              ? "border-warning/25 bg-warning/15 text-warning"
                              : "border-blue-500/25 bg-blue-500/15 text-blue-500",
                          )}
                        >
                          {isManual ? "Manual" : "Auto"}
                        </Badge>
                      </TableCell>
                      <TableCell
                        className={cn(
                          "whitespace-nowrap text-right text-sm font-bold tabular-nums",
                          t.type === "income" ? "text-success" : "text-destructive",
                        )}
                      >
                        {t.type === "income" ? "+" : "−"}
                        {fmtMoney(Math.abs(t.amount))}
                      </TableCell>
                      <TableCell className="text-right">
                        {isManual && t.manual_id ? (
                          <div className="inline-flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7"
                              aria-label="Edit transaction"
                              onClick={() => openEdit(t.manual_id!)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-destructive"
                              aria-label="Delete transaction"
                              onClick={() => deleteManualTransaction.mutate(t.manual_id!)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">{t.source_label}</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={4} className="text-right text-sm font-semibold text-muted-foreground">
                    Income · Expenses · Net
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    <span className="font-bold text-success">+{fmtMoney(totals.income)}</span>
                    <br />
                    <span className="font-bold text-destructive">−{fmtMoney(totals.expense)}</span>
                    <br />
                    <span className={cn("font-extrabold", totals.net >= 0 ? "text-success" : "text-destructive")}>
                      {fmtMoney(totals.net)}
                    </span>
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableFooter>
            </Table>
          </div>
        )}
      </Card>

      <TransactionFormDialog open={dialogOpen} onOpenChange={setDialogOpen} year={year} editing={editing} />
    </div>
  );
}
